/**
 * Massiel's on-demand reconciliation view (phase 6 doc, "Roles").
 *
 * The nav link to this screen is always shown to every signed-in account -
 * decision D-7 means the frontend is never told anyone's role, so there is no
 * flag to hide the link on in the first place. This screen renders exactly
 * what `GET /cash-drop/reconciliation` answers: a `submitter` gets the
 * router's own 403 message rendered as content, a `reconciliation_viewer`
 * gets the table. Neither is a client-side guess about who is signed in.
 *
 * Supports the date-range filtering CLAUDE.md calls for ("Massiel's actual
 * usage pattern is reviewing on-demand ... not a scheduled push"). Nothing
 * here computes a money figure - every number rendered is exactly what the
 * backend returned.
 */
import { useEffect, useState } from "react";

import {
  fetchReconciliation,
  formatDetail,
  type Observed,
  type ReconciliationOut,
} from "../api/public";
import type { TokenGetter } from "../auth/session";
import { ReconciliationNav } from "../components/ReconciliationNav";
import { SessionGate } from "../components/SessionGate";

export function Reconciliation() {
  return (
    <>
      <ReconciliationNav />
      <SessionGate>{(getToken) => <ReconciliationView getToken={getToken} />}</SessionGate>
    </>
  );
}

type FetchState =
  /** No result has ever come back yet - the only time the whole screen is
   * replaced with a bare loading message. */
  | { kind: "loading" }
  /** At least one result has landed. `refetching` is true while a NEW request
   * (the manager changed a date filter) is in flight; the previous result -
   * including the filter inputs below it - stays on screen rather than being
   * torn down, so editing "From" doesn't wipe the "To" field before the
   * manager can reach it. */
  | { kind: "loaded"; result: Observed<ReconciliationOut>; refetching: boolean };

function ReconciliationView({ getToken }: { getToken: TokenGetter }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [state, setState] = useState<FetchState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => (prev.kind === "loaded" ? { ...prev, refetching: true } : { kind: "loading" }));
    (async () => {
      const token = await getToken();
      const result = await fetchReconciliation({ dateFrom, dateTo }, token);
      if (!cancelled) setState({ kind: "loaded", result, refetching: false });
    })();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, getToken]);

  if (state.kind === "loading") {
    return <p className="muted">Loading reconciliation…</p>;
  }

  const { result, refetching } = state;
  const detail =
    result.body && typeof result.body === "object" && "detail" in result.body
      ? formatDetail(result.body.detail)
      : "";

  if (result.status === 403) {
    // Expected for most accounts (`submitter`) - the server's own message
    // already names the committed file, not a database row (phase 3 doc), so
    // it's shown as-is rather than replaced with our own wording.
    return (
      <section className="card" data-testid="reconciliation-refused">
        <h2>Reconciliation</h2>
        <p>{detail || "You do not have access to the reconciliation view."}</p>
      </section>
    );
  }

  if (!result.ok || !result.body || !("rows" in result.body)) {
    return (
      <section className="card error" data-testid="reconciliation-error">
        <h2>Reconciliation</h2>
        <p>{detail || `Request failed (${result.status}).`}</p>
      </section>
    );
  }

  const data: ReconciliationOut = result.body;

  return (
    <>
      <section className="card">
        <h2>Reconciliation</h2>
        <div className="filters">
          <label className="field">
            From
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label className="field">
            To
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>
        </div>
        {refetching && <p className="muted">Refreshing…</p>}
      </section>

      <section className="card" data-testid="reconciliation-table">
        {data.rows.length === 0 ? (
          <p className="muted">No submissions in this range.</p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Location</th>
                  <th>Company</th>
                  <th>BUSTYP</th>
                  <th>Submissions</th>
                  <th>Total cash (Z-report)</th>
                  <th>Tip</th>
                  <th>Bank deposit</th>
                  <th>Undeposited</th>
                  <th>Submitted by</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={`${row.drop_date}-${row.location}`}>
                    <td>{row.drop_date}</td>
                    <td>
                      {row.location}
                      {row.shared_bank_account_group && (
                        <span className="tag" title="Shares a physical bank account">
                          shared
                        </span>
                      )}
                    </td>
                    <td>{row.bc_company}</td>
                    <td>{row.bustyp ?? "—"}</td>
                    <td>{row.submissions}</td>
                    <td>{row.till}</td>
                    <td>{row.tip}</td>
                    <td>
                      {row.bank_deposit_amount}
                      {row.deposit_sources.includes("computed") && (
                        <span
                          className="tag"
                          title="Submitted before the deposit became an entered figure; the amount was computed by the old round-down rule"
                        >
                          legacy
                        </span>
                      )}
                    </td>
                    <td>{row.undeposited_balance}</td>
                    <td>{row.submitted_by.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {data.shared_deposits.length > 0 && (
        <section className="card" data-testid="shared-deposits">
          <h2>Shared bank deposits</h2>
          <p className="muted">
            FL Ave and Ybor deposit into the same physical bank account; these totals are what
            should match the single bank line for that day.
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Locations</th>
                  <th>Bank deposit</th>
                </tr>
              </thead>
              <tbody>
                {data.shared_deposits.map((group) => (
                  <tr key={`${group.drop_date}-${group.shared_bank_account_group}`}>
                    <td>{group.drop_date}</td>
                    <td>{group.locations.join(" + ")}</td>
                    <td>{group.bank_deposit_amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
